<template>
  <div class="wrapper__detail">
    <q-linear-progress v-if="this.reloading" indeterminate />
    
    <div class="layout-container">
      <!-- Левая часть: Header + Schema -->
      <div class="layout-left">
        <div class="header">
          <div class="header__top">
            <div style="display: flex; align-items: center; gap: 15px; flex-wrap: wrap;">
              <BackBtn @click="this.handleBack" />
          
          <!-- Квартиры -->
          <q-btn-toggle v-if="this.apartments?.length" v-model="this.activeFloorFlatId" 
            toggle-color="primary" :options="getFlatOptions()" @update:model-value="onFlatChange" size="sm" />
          
          <!-- Комнаты -->
          <CustomSelect v-if="!!this.floorMapId" 
            :options="this.activeFlat ? this.flatRooms?.rooms || [] : this.floorRooms" 
            placeholder="Комната"
            optionValueKey="room_id" 
            optionLabelKey="room_name" 
            :value="this.updateParamBody?.room_id || ''"
            @change="value => this.onUpdateParamBodyChange('room_id', value)" 
            style="width: 120px;" />
          
          <!-- Действия -->
          <CustomDropdown v-if="!!this.floorMapId && !this.editingMode" 
            :onClick="this.handleAction" 
            :options="this.actions || []"
            title="Действия" />
          
          <CustomSelect v-if="this.editingMode" 
            :options="this.paramTypes || []" 
            label="Тип"
            optionValueKey="floor_param_type_id" 
            optionLabelKey="floor_param_type_name"
            :value="this.updateParamBody.floor_param_type_id || ''" 
            style="width: 180px;"
            @change="value => this.onUpdateParamBodyChange('floor_param_type_id', value)" />
          
          <!-- Квартирный лист -->
          <Clickable v-if="this.apartments?.length" 
            :disable="!this.activeFlat?.kvlist" 
            :link="true"
            :href="`${PAGES.imageUrl}?url=${this.activeFlat?.kvlist}&title=${this.activeData?.resident_name}, ${this.activeData?.entrance_name}, этаж ${this.floorMapInfo?.level_num || this.activeData?.floor}, кв ${this.activeFlat?.flat_num}`"
            class="header__link">
            <q-btn size="sm" color="primary" :disable="!this.activeFlat?.kvlist">
              <q-tooltip> Квартирный лист </q-tooltip>
              <q-icon size="2em" color="#fff" name="list_alt_icon" />
            </q-btn>
          </Clickable>
        </div>
      </div>

      <div class="header__head" style="width: 100%;">
        <div style="display: flex; gap: 15px; align-items: center;">
          <!-- Переключатели режимов с иконками -->
          <template v-if="!!this.floorMapId">
            <!-- Редактирование -->
            <div class="toggle-with-icon">
              <q-icon 
                name="edit" 
                size="20px" 
                :color="!this.activeFloorFlatId ? 'grey' : 'green'"
                :class="{ 'icon-disabled': !this.activeFloorFlatId }">
                <q-tooltip>Редактирование</q-tooltip>
              </q-icon>
              <q-toggle
                v-model="editingMode"
                :disable="!this.activeFloorFlatId"
                color="primary"
                size="xs"
              />
            </div>

            <!-- Групповое редактирование -->
            <div class="toggle-with-icon">
              <q-icon 
                name="edit_note" 
                size="20px" 
                :color="!this.editingMode ? 'grey' : 'green'"
                :class="{ 'icon-disabled': !this.editingMode }">
                <q-tooltip>Групповое редактирование</q-tooltip>
              </q-icon>
              <q-toggle
                v-model="groupEditingMode"
                :disable="!this.editingMode"
                color="primary"
                size="xs"
              />
            </div>

            <!-- Удаление -->
            <div class="toggle-with-icon">
              <q-icon 
                name="delete_outline" 
                size="20px" 
                color="green">
                <q-tooltip>Удаление</q-tooltip>
              </q-icon>
              <q-toggle
                v-model="deletingMode"
                color="red"
                size="xs"
              />
            </div>

            <!-- Границы квартир -->
            <div class="toggle-with-icon">
              <q-icon 
                name="border_outer" 
                size="20px" 
                :color="this.editingMode ? 'grey' : 'green'"
                :class="{ 'icon-disabled': this.editingMode }">
                <q-tooltip>Границы квартир</q-tooltip>
              </q-icon>
              <q-toggle
                v-model="flatBorders"
                :disable="this.editingMode"
                color="primary"
                size="xs"
              />
            </div>
          </template>

          <!-- Разделитель -->
          <div v-if="!!this.floorMapId && this.getFloorMapResourceCode() === 'AUTOCAD'" class="header__divider"></div>

          <!-- Кнопка загрузки из Autocad (только для AUTOCAD проектов) -->
          <q-btn v-if="!!this.floorMapId && this.getFloorMapResourceCode() === 'AUTOCAD'" 
            size="sm" padding="xs" title="Загрузить с буффера" label="Загрузить с буффера" color="green"
            :loading="this.bufferLoading" @click="this.getBufferData" />

          <!-- Кнопка проверки ошибок (только для AUTOCAD проектов) -->
          <q-btn v-if="!!this.floorMapId && this.getFloorMapResourceCode() === 'AUTOCAD'" 
            size="sm" padding="xs" label="Проверить ошибки" color="red" 
            :loading="this.errorLoading" @click="this.getFloorErrors" />

          <!-- Кнопка скрыть/показать легенду -->
          <q-btn 
            v-if="!!this.floorMapId"
            size="sm" 
            color="primary"
            :icon="legendCollapsed ? 'chevron_left' : 'chevron_right'"
            round
            outline
            @click="legendCollapsed = !legendCollapsed">
            <q-tooltip>{{ legendCollapsed ? 'Показать' : 'Скрыть' }} легенду</q-tooltip>
          </q-btn>

          <!-- Кнопка изменения схемы этажа -->
          <q-btn v-if="!this.floorMapId && !this.deletingMode" size="sm" title="Изменить схему этажа"
            label="Изменить схему этажа" color="primary" @click="this.handleFloorMapChange" />
        </div>
      </div>
    </div>
        <Schema :floorSchema="floorSchema" :floorMapInfo="this.floorMapInfo"
          :schemaFetching="this.schemaFetching" :activeFloorFlatId="this.activeFloorFlatId" :flatBorders="this.flatBorders"
          :floorMapId="this.floorMapId" :params="this.params" :getFloorSchemaData="this.getFloorSchemaData"
          :getBufferParamsData="this.getBufferParamsData" :bufferParams="this.bufferParams"
          :getFloorLegendData="this.getFloorLegendData" :updateFloorSchema="this.updateFloorSchema"
          :activeData="this.activeData" :editingMode="this.editingMode" :deletingMode="this.deletingMode"
          :updateParamBody="this.updateParamBody" :updateData="this.updateData"
          :activeFloorParamTypeIds="this.activeFloorParamTypeIds" :activeFlat="this.activeFlat"
          :groupEditingMode="this.groupEditingMode" />
      </div>

      <!-- Правая часть: Legend -->
      <div class="layout-right" v-if="!!this.floorMapId && !legendCollapsed">
        <Legend :legend-fetching="this.legendFetching"
          :legend-info="this.legendInfo" :selectable="true" :reloadData="this.getFloorLegendData"
          :selected="this.activeFloorParamTypeIds" :onBlock="this.blockFloorParamType"
          :onSelect="value => this.setActiveParamTypeId(value)"
          :onAdd="this.handleAddFloorFlatParamValue"
          :onDelete="this.handleDeleteFloorFlatParamValue"
          :canDelete="!!(this.activeFlat?.floor_flat_id && this.updateParamBody?.room_id)" />
      </div>
    </div>
    
    <!-- Статусная строка -->
    <div class="status-bar">
      <div class="status-bar__item status-bar__item--with-action" v-if="!!this.activeData?.resident_name">
        <q-icon name="business" size="18px" />
        <span>{{ this.activeData?.resident_name }}</span>
        <q-icon 
          name="replay" 
          size="18px" 
          class="reload-btn" 
          @click="this.reloadData"
          style="cursor: pointer; margin-left: 8px;"
        >
          <q-tooltip>Обновить данные</q-tooltip>
        </q-icon>
      </div>
      <div class="status-bar__item" v-if="!!this.activeData?.entrance_name">
        <q-icon name="meeting_room" size="18px" />
        <span>{{ this.activeData?.entrance_name }}</span>
      </div>
      <div class="status-bar__item">
        <q-icon name="apartment" size="18px" />
        <span>Этаж: {{ this.floorMapInfo?.level_num || this.activeData?.floor || '-' }}</span>
      </div>
      <div class="status-bar__item" v-if="!!this.activeData?.project_type_name">
        <q-icon name="category" size="18px" />
        <span>{{ this.activeData?.project_type_name }}</span>
      </div>
      <div class="status-bar__item" v-if="this.floorMapId">
        <span>Floor Map ID: {{ this.floorMapId }}</span>
      </div>
      <div class="status-bar__item" v-if="this.floorMapInfo?.external_resource_code">
        <q-icon name="grid_on" size="18px" />
        <span>{{ this.getProjectTypeLabel() }}</span>
      </div>
    </div>

    <q-dialog v-model="copyToProjectModal" persistent>
      <q-card style="min-width: 360px;">
        <q-card-section>
          <div class="text-h6">Перенести этаж в другой проект</div>
        </q-card-section>
        <q-card-section>
          <CustomSelect
            :options="copyToProjectProjectOptions"
            option-value-key="project_id"
            option-label-key="label"
            :value="copyToProjectTargetProjectId"
            @change="v => (copyToProjectTargetProjectId = v)"
            placeholder="Выберите проект"
            :loading="copyToProjectLoading"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" v-close-popup />
          <q-btn unelevated label="Перенести" color="primary" :loading="copyToProjectLoading" :disable="!copyToProjectTargetProjectId" @click="submitCopyToProject" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';
import BackBtn from '@/components/common/BackBtn.vue';
import Clickable from '@/components/common/Clickable.vue';
import CustomDropdown from "@/components/common/CustomDropdown";
import CustomSelect from "@/components/form/CustomSelect";
import CustomPopup from '@/components/common/CustomPopup';
import { API_URL, MODAL_NAMES, PAGES } from '@/constants';
import Legend from "./Legend.vue";
import Schema from "./Schema.vue";
import { blockParamType, getBufferParams, getFloorFlatRooms, getFloorLegendInfo, getFloorMapInfo, getFloorParamTypes, getFloorRooms, getFloorSchema, recalculateFloorValues, getFloorMapErrors, getRevitFloorList, getRevitData, deleteFloorFlatParamValue, getProjectList, copyFloorToProject } from './services';
import { useAppStore } from '@/services/store/app';

export default {
  name: "ResidentialFloorDetail",
  components: { BackBtn, Schema, CustomDropdown, CustomSelect, Legend, Clickable, CustomPopup },
  props: ['activeData', 'params', 'updateData'],
  data() {
    return {
      PAGES,
      schemaFetching: false,
      editingMode: false,
      groupEditingMode: false,
      deletingMode: false,
      reloading: false,
      flatRooms: null,
      floorRooms: null,
      paramTypes: [],
      updateParamBody: { room_id: null, floor_param_type_id: null },
      floorSchema: null,
      legendInfo: null,
      legendFetching: false,
      floorMapId: null,
      floorMapInfo: null,
      objectInfo: null,
      activeFloorFlatId: null,
      activeFlat: null,
      flatBorders: false,
      bufferParams: [],
      activeFloorParamTypeIds: [],
      errorLoading: false,
      bufferLoading: false,
      revitLoading: false,
      revitLevelOptions: [],
      revitLevelNumber: '',
      legendCollapsed: false,
      copyToProjectModal: false,
      copyToProjectTargetProjectId: null,
      copyToProjectProjects: [],
      copyToProjectLoading: false,
    };
  },
  computed: {
    apartments() {
      return this.floorSchema?.flat?.map(item => ({ ...item, label: `кв. ${item.flat_num}` }))
    },
    actions() {
      return [
        { label: 'Пересчитать', value: 'recalculate', icon: 'calculate', disabled: !!this.legendFetching, onClick: this.recalculateValues },
        { label: 'Заменить по стояку', value: 'flat-grouping', icon: 'apartment', disabled: !this.activeFloorFlatId, onClick: this.handleFlatGrouping },
        { label: 'Заменить по этажу', value: 'floor-grouping', icon: 'layers', onClick: this.handleGrouping, disabled: !this.activeData?.colour_id },
        { label: 'Редактировать', value: 'edit', icon: 'edit', onClick: () => this.handleFloorMapChange(true) },
        { label: 'Скачать исходники', value: 'autocad', icon: 'download', link: true, href: API_URL + this.floorSchema?.floor_map?.autocad_url, disabled: !this.floorSchema?.floor_map?.autocad_url },
        { label: 'Перенести в другой проект', value: 'copy_to_project', icon: 'drive_file_move', onClick: this.openCopyToProjectModal },
      ]
    },
    bufferParamsExist() {
      return this.bufferParams?.lines?.length || this.bufferParams?.circles?.length
    },
    copyToProjectProjectOptions() {
      const currentId = this.activeData?.project_id
      return (this.copyToProjectProjects || [])
        .filter((p) => p.project_id != null && Number(p.project_id) !== Number(currentId))
        .map((p) => ({ ...p, label: p.project_name || `Проект ${p.project_id}` }))
    },
  },
  methods: {
    getFloorMapResourceCode() {
      return this.floorMapInfo?.external_resource_code || null
    },
    getProjectTypeLabel() {
      const resourceCode = this.floorMapInfo?.external_resource_code
      if (resourceCode === 'AUTOCAD') {
        return 'AUTOCAD'
      } else if (resourceCode) {
        return 'REVIT'
      }
      return 'Неизвестно'
    },
    getProjectTypeBadgeColor() {
      const resourceCode = this.floorMapInfo?.external_resource_code
      if (resourceCode === 'AUTOCAD') {
        return 'blue'
      } else if (resourceCode) {
        return 'deep-purple'
      }
      return 'grey'
    },
    onActiveParamTypesChange(ids) {
      this.activeFloorParamTypeIds = ids || []
    },
    setActiveParamTypeId(typeId) {
      if (this.activeFloorParamTypeIds.includes(typeId))
        return this.onActiveParamTypesChange(this.activeFloorParamTypeIds.filter(item => item !== typeId))
      this.onActiveParamTypesChange([...this.activeFloorParamTypeIds, typeId])
    },
    async getFloorSchemaData(floor_map_id) {
      this.schemaFetching = true
      const res = await getFloorSchema(floor_map_id)
      this.schemaFetching = false
      this.floorSchema = res || null
    },
    async getFloorLegendData() {
      await this.getFloorLegend(this.floorMapId, this.activeFlat?.floor_flat_id || null, this.updateParamBody?.room_id || null)
    },
    async getFloorLegend(floor_map_id, floor_flat_id = '', room_id = '') {
      this.legendFetching = true
      const res = await getFloorLegendInfo({ floor_map_id, floor_flat_id, room_id, project_type_id: this.activeData.project_type_id })
      this.legendFetching = false
      this.legendInfo = res || null
    },
    async recalculateValues() {
      if (this.legendFetching) return
      this.legendFetching = true
      const res = await recalculateFloorValues(this.floorMapI, { project_type_id: this.activeData.project_type_id })
      this.legendFetching = false
      this.legendInfo = res || null
    },
    async blockFloorParamType(floor_param_type_id = null, all = false) {
      const params = {
        floor_map_id: this.floorMapId, floor_flat_id: this.activeFlat?.floor_flat_id || null, room_id: this.updateParamBody?.room_id || null
      }
      const body = {
        floor_map_id: this.floorMapId,
        floor_param_type_id, all
      }
      this.legendFetching = true
      const res = await blockParamType(body, params)
      this.legendFetching = false
      this.legendInfo = res || null
    },
    async getFloorRoomsData() {
      const res = await getFloorRooms(this.floorMapId)
      this.floorRooms = res || []
    },
    async getFlatRooms(floor_flat_id = '') {
      if (!floor_flat_id) return
      const res = await getFloorFlatRooms(floor_flat_id)
      this.flatRooms = { floor_flat_id, rooms: res || [] }
    },
    async getFloorFloorMap(floor_map_id) {
      if (!floor_map_id) return
      const res = await getFloorMapInfo(floor_map_id)
      this.floorMapInfo = res
    },
    async getParamTypes() {
      const res = await getFloorParamTypes()
      this.paramTypes = res
    },
    async getBufferParamsData() {
      const res = await getBufferParams(this.floorMapId)
      this.bufferParams = res
      if (res?.length)
        this.editingMode = true
    },
    handleUpdateBufferParams(data) {
      this.bufferParams = data
      if (data?.lines?.length || data?.circles?.length) {
        this.editingMode = true
      }
    },
    getFlatOptions() {
      const all = {
        label: 'Все',
        floor_flat_id: null,
        value: null,
      }
      if (!this.apartments?.length) return [all]
      return [all, ...this.apartments?.map(item => ({ ...item, value: item.floor_flat_id }))]
    },
    onFlatChange(floor_flat_id, flat) {
      if (!floor_flat_id) {
        this.editingMode = false
        this.activeFlat = null
        this.activeFloorFlatId = null || null
        this.getFloorLegendData()
        return
      }
      this.activeFlat = flat
      this.activeFloorFlatId = floor_flat_id || null
      this.flatBorders = false
      this.getFloorLegendData()
    },
    onUpdateParamBodyChange(key, value) {
      this.updateParamBody[key] = value
      if (key === 'room_id') {
        this.getFloorLegendData()
      }
    },
    updateFloorSchema(res) {
      if (res?.floor_map?.floor_map_id) {
        this.floorMapId = res?.floor_map?.floor_map_id
        this.floorMapInfo = res?.floor_map
      }
      this.floorSchema = res
      this.updateData()
    },
    handleBack() {
      this.floorSchema = null
      this.$emit('goBack')
    },
    handleGrouping() {
      this.showModal(MODAL_NAMES.RESIDENTIAL_GROUPING, {
        title: 'Выберите этажи для смены',
        mode: 'floor-grouping',
        params: { ...this.params, floor_map_id: this.floorMapId },
        data: { colour_id: this.activeData?.colour_id },
        floor_map_resource_code: this.getFloorMapResourceCode(),
        onSubmit: this.updateFloorSchema,
      })
    },
    handleFloorMapChange(isEdit = false) {
      let body = { level_num: this.activeData.floor, entrance: this.activeData.entrance, resident_id: this.activeData.resident_id }

      if (isEdit === true && this.floorMapInfo) {
        body = { ...body, ...this.floorMapInfo }
      }

      this.showModal(MODAL_NAMES.AddFloorMap, {
        title: 'Изменить схему этажа',
        params: { entrance: this.activeData.entrance, resident_id: this.activeData.resident_id, project_type_id: this.activeData.project_type_id },
        body, isEdit: isEdit === true, floor_map_id: this.floorMapId,
        onSubmit: this.updateFloorSchema,
      })
    },
    handleFlatGrouping() {
      if (!this.activeFlat) return
      this.showModal(MODAL_NAMES.RESIDENTIAL_GROUPING, {
        title: 'Выберите этажи для смены',
        mode: 'flat-grouping',
        params: { ...this.params, floor_map_id: this.floorMapId },
        data: { ...this.activeData, floorFlatLength: this.floorSchema?.flat?.length, ...this.activeFlat, },
        onSubmit: (res) => {
          this.updateFloorSchema(res)
        },
      })
    },
    handleAction(action) {
      if (!action?.onClick) return
      action.onClick()
    },
    openCopyToProjectModal() {
      this.copyToProjectModal = true
      this.copyToProjectTargetProjectId = null
      this.loadCopyToProjectProjects()
    },
    async loadCopyToProjectProjects() {
      const resident_id = this.activeData?.resident_id
      const project_type_id = this.activeData?.project_type_id
      if (!resident_id || !project_type_id) {
        this.copyToProjectProjects = []
        return
      }
      this.copyToProjectLoading = true
      try {
        const list = await getProjectList({ project_type_id, resident_id, is_sign: 0 })
        this.copyToProjectProjects = Array.isArray(list) ? list : []
      } catch (e) {
        this.copyToProjectProjects = []
      }
      this.copyToProjectLoading = false
    },
    async submitCopyToProject() {
      if (!this.copyToProjectTargetProjectId || !this.floorMapId) return
      this.copyToProjectLoading = true
      try {
        await copyFloorToProject(this.floorMapId, { target_project_id: this.copyToProjectTargetProjectId })
        this.showSucceedSnackbar('Запрос на перенос этажа выполнен. После подключения функции в БД перенос будет выполняться.')
        this.copyToProjectModal = false
      } catch (e) {
        this.showErrorSnackbar(e?.response?.data?.error || e?.message || 'Ошибка при переносе этажа')
      }
      this.copyToProjectLoading = false
    },
    async reloadData() {
      if (this.reloading) return
      this.reloading = true
      await this.getFloorSchemaData(this.activeData.floor_map_id)
      this.getFloorLegendData()
      await this.getFloorFloorMap(this.activeData.floor_map_id)
      this.reloading = false
    },
    async loadRevitFloorList() {
      this.revitLoading = true
      const revitFloorList = await getRevitFloorList({ floor_map_id: this.floorMapId })
      this.revitLevelOptions = revitFloorList?.data || []
      this.revitLoading = false
    },
    async handleChangeRevitLevel(value) {
      this.revitLevelNumber = value

      if (!value) return

      this.revitLoading = true
      const revitData = await getRevitData({
        floor_map_id: this.floorMapId,
        level_number: value
      })
      this.revitLoading = false

      const lines = revitData.data.filter((item) => item.coord_type !== "POINT")
      const circles = revitData.data.filter((item) => item.coord_type === "POINT")

      this.handleUpdateBufferParams({ lines, circles })

      this.showSucceedSnackbar('Данные из Revit успешно загружены')
    },
    async getBufferData() {
      this.bufferLoading = true
      await this.getBufferParamsData()
      this.bufferLoading = false
    },
    async getFloorErrors() {
      this.errorLoading = true
      const res = await getFloorMapErrors(this.floorMapId)
      this.errorLoading = false
      if (!res) return
      const { empty_second_param_type, line_error } = res
      if (!empty_second_param_type && !line_error)
        return this.showSucceedSnackbar('Ошибок не найдено')

      this.showModal(MODAL_NAMES.FLOOR_MAP_ERRORS, {
        title: 'Список ошибок:',
        data: res
      })
    },
    handleAddFloorFlatParamValue() {
      if (!this.activeFlat?.floor_flat_id) {
        this.showSucceedSnackbar('Выберите квартиру')
        return
      }

      this.showModal(MODAL_NAMES.AddFloorFlatParamValue, {
        title: 'Добавить параметр квартиры',
        floor_flat_id: this.activeFlat.floor_flat_id,
        onSuccess: () => {
          this.getFloorLegendData()
        }
      })
    },
    async handleDeleteFloorFlatParamValue(item) {
      const floor_flat_id = this.activeFlat?.floor_flat_id
      const room_id = this.updateParamBody?.room_id
      if (!floor_flat_id || !room_id) {
        this.showSucceedSnackbar('Выберите квартиру и комнату для удаления записи')
        return
      }
      if (!item?.floor_param_type_id) return
      try {
        await deleteFloorFlatParamValue({
          floor_flat_id,
          room_id,
          floor_param_type_id: item.floor_param_type_id
        })
        this.showSucceedSnackbar('Запись удалена')
        this.getFloorLegendData()
      } catch (e) {
        console.error('Ошибка при удалении параметра квартиры:', e)
      }
    },
  },
  watch: {
    activeFlat(newVal) {
      if (newVal) {
        if (this.flatRooms?.floor_flat_id !== newVal?.floor_flat_id) {
          this.getFlatRooms(newVal?.floor_flat_id)
        }
      }
    },
    editingMode(newValue) {
      if (newValue) {
        this.deletingMode = false;
        this.flatBorders = false;
      }
      if (newValue && !this.paramTypes?.length) {
        this.getParamTypes()
      }
      if (!newValue)
        this.groupEditingMode = false
    },
    deletingMode(newValue) {
      if (newValue) {
        this.editingMode = false;
        this.groupEditingMode = false;
        this.flatBorders = false;
      }
    },
    flatBorders(newValue) {
      if (newValue) {
        this.editingMode = false;
        this.deletingMode = false;
        this.groupEditingMode = false;
      }
    },
  },
  mounted() {
    this.floorMapId = this.activeData.floor_map_id
    this.getFloorFloorMap(this.activeData.floor_map_id)
    this.getFloorSchemaData(this.activeData.floor_map_id)
    this.getFloorLegend(this.activeData.floor_map_id)
    this.getFloorRoomsData()
    this.loadRevitFloorList()
  },
  setup() {
    const { showModal } = useAppStore()
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar()
    return { showSucceedSnackbar, showErrorSnackbar, showModal }
  }
};
</script>

<style scoped lang="scss">
.wrapper__detail {
  position: relative;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.layout-container {
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 0;
}

.layout-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding-bottom: 40px; /* для статусной строки */
  border-right: 4px solid #1e40af;
}

.layout-right {
  width: 320px;
  background-color: #f9fafb;
  overflow-y: auto;
  padding: 10px;
}

.project-type-badge {
  font-size: 11px;
  padding: 4px 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header {
  padding: 20px;
  border-bottom: 4px solid #1e40af;
  background-color: #ffffff;

  &__link {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;

    i {
      display: inline-block;
    }
  }
}

.header__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;
  position: relative;
}

.header__top {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
}

.header__divider {
  width: 1px;
  height: 30px;
  background-color: #d1d5db;
  margin: 0 5px;
}

.revit-popup {
  padding: 10px;
}

.toggle-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-disabled {
  opacity: 0.4;
}

.title {
  font-size: 24px;
  line-height: 24px;
  font-weight: 500;
  margin-top: 0;
  margin-bottom: 0;
  cursor: pointer;
  transition: all .3s;
  color: #4B5563;

  &:hover {
    color: rgb(5, 104, 0);
  }
}

.reloadIcon {
  font-size: 20px;
  color: rgb(5, 104, 0);
}

.resident {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  line-height: 12px;
  color: #9CA3AF;
  margin-top: 3px;

  span {
    font-size: 12px;
    line-height: 12px;
    color: #9CA3AF
  }
}

.tooltip {
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 5px;
  box-shadow: none;

  & div {
    font-size: 11px;
    line-height: 1;
  }
}

.status-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 32px;
  background-color: #1e293b;
  color: #e2e8f0;
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 20px;
  font-size: 13px;
  z-index: 1000;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.1);

  &__item {
    display: flex;
    align-items: center;
    gap: 6px;
    
    .q-icon {
      color: #94a3b8;
    }

    span {
      color: #e2e8f0;
      font-weight: 400;
    }

    &:not(:last-child)::after {
      content: '';
      width: 1px;
      height: 16px;
      background-color: #475569;
      margin-left: 20px;
    }
  }

  .reload-btn {
    color: #10b981;
    transition: all 0.2s;
    
    &:hover {
      color: #34d399;
      transform: rotate(180deg);
    }
    
    &:active {
      transform: scale(0.9) rotate(180deg);
    }
  }
}
</style>
